import { ReportPeriod, ReportType } from "@/components/common/report-chip";
import { DsdFile } from "@/types/dsd";
import { ReportGroupData, ReportVersion } from "./types";

// ============================================
// DsdFile 목록을 화면용 보고서 그룹으로 변환하는 순수 함수.
// prevWorkbookId(직전 버전 링크)를 따라 워크북들을 하나의 버전 스택으로 묶는다.
// 최신본(= 다른 워크북의 직전본으로 지목되지 않은 leaf)에서 prevWorkbookId 를 따라
// 과거로 걸어 체인을 만든다. 링크가 목록 밖을 가리키면 거기서 체인을 끊는다.
// 번호는 여기서 계산하지 않고 서버가 준 versionNo 를 그대로 쓴다 — 체인 위치로 매기면
// 중간 버전이 삭제될 때 뒷 번호가 밀려 유저가 기억하는 V번호가 다른 문서를 가리킨다.
// 그래서 삭제된 버전의 번호는 구멍으로 남는다(V1, V3 …).
// ============================================

export const asReportType = (value: string): ReportType | undefined =>
  value === "CONSOLIDATED" || value === "SEPARATE" ? value : undefined;

export const asReportPeriod = (value: string | null): ReportPeriod | undefined =>
  value === "Q1" || value === "Q2" || value === "Q3" || value === "Q4"
    ? value
    : undefined;

export interface GroupReportsDeps {
  /** 작성자 이메일 → 표시 이름 */
  resolveAuthorName: (email: string) => string;
  /** ISO 문자열 → "2026.09.28" */
  formatDate: (iso: string) => string;
  /** ISO 문자열 → "3개월 전" */
  formatRelativeTime: (iso: string) => string;
}

export const groupReports = (
  files: DsdFile[],
  deps: GroupReportsDeps,
): ReportGroupData[] => {
  const byId = new Map(files.map((file) => [file.id, file]));

  // 목록 안에서 누군가의 직전본으로 지목된 id. 여기 없는 워크북이 체인의 최신(leaf).
  const referencedAsPrev = new Set<string>();
  files.forEach((file) => {
    if (file.prevWorkbookId && byId.has(file.prevWorkbookId)) {
      referencedAsPrev.add(file.prevWorkbookId);
    }
  });

  // label 은 useDSDFiles 의 addDisplayLabel 이 만든 표시용 이름 — 같은 이름이 여러 건이면
  // `(revision)` 을 붙여 구분한다. displayName ?? name 을 직접 쓰면 동명 보고서가
  // 목록에서 완전히 똑같이 보인다(신규 목록엔 회사명 컬럼도 없다).
  const toVersion = (file: DsdFile): ReportVersion => ({
    id: file.id,
    name: file.label,
    version: `V${file.versionNo}`,
    date: deps.formatDate(file.createdAt),
    author: { name: deps.resolveAuthorName(file.creator), email: file.creator },
    relativeTime: deps.formatRelativeTime(file.updatedAt),
  });

  // 최신본에서 prevWorkbookId 를 따라 과거로 걸어 [최신, …, 루트] 체인을 만든다.
  // seen 가드로 순환을, byId 조회로 목록 밖 링크(삭제된 직전본 등)를 안전하게 끊는다.
  const buildChain = (latest: DsdFile): DsdFile[] => {
    const chain: DsdFile[] = [];
    const seen = new Set<string>();
    let cursor: DsdFile | undefined = latest;
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      chain.push(cursor);
      cursor = cursor.prevWorkbookId
        ? byId.get(cursor.prevWorkbookId)
        : undefined;
    }
    return chain;
  };

  const latests = files.filter((file) => !referencedAsPrev.has(file.id));

  return latests.map((latest) => {
    const chain = buildChain(latest); // [최신, …, 루트]
    const olderVersions = chain.slice(1).map((file) => toVersion(file));

    return {
      key: latest.id,
      financialStatementType: asReportType(latest.financialStatementType),
      reportPeriod: asReportPeriod(latest.reportQuarter),
      latest: toVersion(latest),
      olderVersions,
    };
  });
};
